<template>
  <div
    class="city-panel"
    :class="[
      `city-panel--${layout}`,
      {
        'is-field-leading': isFieldLeading,
        'is-locate-collapsed': isLocateCollapsed,
        'is-search-only': isSearchOnly,
      },
    ]"
  >
    <!-- Outside the search block, which travels to the foot of the panel: the
         close button belongs in the corner, so it is taken out of the flow and
         pinned there instead of being carried along. -->
    <button
      class="city-panel__close"
      type="button"
      v-if="closable"
      :aria-label="texts.close"
      @click="close"
    >
      <svg aria-hidden="true" class="icon"><use href="#xmark" /></svg>
    </button>

    <div class="city-panel__search">
      <p class="city-panel__title" v-text="title" />

      <div
        class="city-panel__query"
        ref="query"
        :style="{ '--city-panel-locate-width': locateWidth }"
      >
        <div class="city-panel__field" :class="{ 'is-filled': query !== '' }">
          <svg aria-hidden="true" class="icon">
            <use href="#magnifying-glass" />
          </svg>
          <input
            aria-autocomplete="list"
            autocomplete="off"
            class="city-panel__input"
            role="combobox"
            type="text"
            ref="search"
            :aria-activedescendant="activeOptionId"
            :aria-controls="resultsId"
            :aria-expanded="isSearching"
            :placeholder="texts.writeYourLocationHere"
            :value="query"
            @blur="isFocused = false"
            @focus="isFocused = true"
            @input="onInput"
            @keydown.down.prevent="moveDown"
            @keydown.enter.prevent="chooseActive"
            @keydown.esc.prevent="clear"
            @keydown.up.prevent="moveUp"
          >
          <button
            class="city-panel__clear"
            type="button"
            v-if="query !== ''"
            :aria-label="texts.clearSearch"
            @click="clear"
          >
            <svg aria-hidden="true" class="icon"><use href="#xmark" /></svg>
          </button>
        </div>

        <!-- In the sheet it is inert rather than unmounted while collapsed, so
             it has something to fold; the attribute falls through to the
             component's root. The dropdown hands the job to its results footer
             instead, where the button has room for its full label. -->
        <CityPanelLocate
          v-if="!isSearching || isSheet"
          :compact="true"
          :inert="isLocateCollapsed || null"
          @select="select"
        />
      </div>
    </div>

    <div class="city-panel__results-group" v-if="isSearching">
      <div class="city-panel__loading" v-if="isLoadingCities">
        <Loader :loading="true" />
      </div>

      <template v-else>
        <p aria-live="polite" class="city-panel__count" v-text="resultCount" />

        <TransitionGroup
          appear
          class="city-panel__results"
          name="city-panel-result"
          role="listbox"
          tag="div"
          v-if="hasResults"
          :id="resultsId"
        >
          <CityPanelResult
            v-for="(city, index) in cities"
            :key="city.choice"
            :active="index === activeIndex"
            :city="city"
            :id="optionId(index)"
            :position="index"
            :query="query"
            :style="stagger(index)"
            @activate="setActive"
            @select="select"
          />
        </TransitionGroup>

        <!-- Under the matches rather than beside the field: the city section
             that names the picked city is folded away while a search is on, and
             the locate button has stepped out of the field's row. The sheet
             needs neither — the drawer's own pill carries the city, and its
             button is pinned on screen beside the field. -->
        <div class="city-panel__footer" v-if="!isSheet">
          <p class="city-panel__current-city" v-if="cityTerm">
            {{ texts.currentCityLabel }}
            <b v-text="cityTerm.name" />
          </p>
          <CityPanelLocate @select="select" />
        </div>
      </template>
    </div>

    <!-- Folded away rather than unmounted, so the fold has something to
         animate; inert keeps its links out of reach while it is closed. `inert`
         is not one of the attributes Vue knows to drop when false, and it is a
         boolean one, so `false` would read as inert wherever the host has no
         property to set instead. -->
    <div
      class="city-panel__current"
      v-if="cityTerm"
      :inert="isFieldLeading || null"
    >
      <p class="city-panel__eyebrow" v-text="texts.yourCity" />

      <div class="city-panel__city">
        <span class="city-panel__city-name">
          <svg aria-hidden="true" class="icon">
            <use href="#location-dot" />
          </svg>
          {{ cityTerm.name }}
        </span>
        <span class="city-panel__badge">
          <svg aria-hidden="true" class="icon"><use href="#check" /></svg>
          {{ texts.selectedCity }}
        </span>
      </div>

      <Loader v-if="isLoading" :loading="true" />
      <TransitionGroup
        appear
        class="city-panel__services"
        name="city-panel-service"
        tag="ul"
        v-else
      >
        <li
          v-for="(service, index) in visibleServices"
          :key="service.key"
          :style="stagger(index)"
        >
          <a
            class="city-panel__service"
            :href="service.url"
            @click="reportServiceClick(service)"
          >
            <span class="city-panel__service-text">
              <span class="city-panel__service-label">{{ service.label }}</span>
              <span class="city-panel__service-title">{{ service.title }}</span>
            </span>
            <svg aria-hidden="true" class="icon">
              <use href="#chevron-right" />
            </svg>
          </a>
        </li>
      </TransitionGroup>

      <button
        class="city-panel__more"
        type="button"
        v-if="!isLoading && hasHiddenServices"
        :aria-expanded="showAllServices"
        @click="toggleServices"
      >
        <span v-text="moreServicesLabel" />
        <svg aria-hidden="true" class="icon city-panel__more-chevron">
          <use href="#chevron-down" />
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
  import CityPanelLocate from "Components/CityPanelLocate.vue";
  import CityPanelResult from "Components/CityPanelResult.vue";
  import Loader from "Components/loader/Loader.vue";
  import { useRoleTermLocalStorage } from "Main/composables/useRoleTermLocalStorage";
  import { buildCityServices, pickPreferredService } from "Utils/city-services";
  import { pushCityServiceClick } from "Utils/city-tracking";
  import debounce from "lodash-es/debounce";
  import { sprintf } from "sprintf-js";
  import { useId } from "vue";
  import { mapMutations, mapState } from "vuex";

  /** Below this the search is not worth a request; matches the old vue-select rule. */
  const MIN_SEARCH_LENGTH = 2;

  /** Matches the height transition in _city-panel.scss. */
  const HEIGHT_DURATION = 240;

  /** Past this the stagger would make the last rows feel slow to arrive. */
  const MAX_STAGGERED_ROWS = 6;
  const SEARCH_DEBOUNCE = 300;

  export default {
    beforeUnmount() {
      this.searchCities.cancel();
      clearTimeout(this.releaseTimer);
    },

    components: {
      CityPanelLocate,
      CityPanelResult,
      Loader,
    },

    computed: {
      ...mapState("cities", {
        cities: "cities",
        cityLinks: "cityLinks",
        hasLoadedCities: "hasLoadedCities",
        isLoadingCities: "isLoading",
        isLoadingCityLinks: "isLoadingCityLinks",
      }),

      activeOptionId() {
        return this.activeIndex === -1 ? null : this.optionId(this.activeIndex);
      },

      /** Only worth a toggle when it hides something. */
      hasHiddenServices() {
        return !!this.preferredService && this.services.length > 1;
      },

      hasResults() {
        return this.cities.length > 0;
      },

      /**
       * The field is pinned at the foot of the panel, so once there are matches
       * to type towards it leads instead, with them under it and the city
       * section folded away.
       *
       * The sheet leads from the moment the field is focused: the phone
       * keyboard opens over that foot, leaving the matches above it in a strip
       * too short to read. It keeps leading while a query stands too — tapping
       * a match blurs the field first, and the layout must not turn over from
       * under the tap. The dropdown waits for the query, since it focuses its
       * field as it opens and would otherwise fold the city section away before
       * it had been read.
       */
      isFieldLeading() {
        return this.isSearching || (this.isSheet && this.isFocused);
      },

      isLoading() {
        return this.isLoadingCityLinks;
      },

      /**
       * Once a search is on, the matches are the whole job and the field wants
       * the row to itself, so the locate button folds out to the right. The
       * sheet's alone: the dropdown unmounts that button for its footer's, and
       * the class would fold the footer's away as it arrived.
       */
      isLocateCollapsed() {
        return this.isSheet && this.isSearching;
      },

      /** The panel swaps the selected city for search results while searching. */
      isSearching() {
        return this.query.trim().length >= MIN_SEARCH_LENGTH;
      },

      /**
       * With no city picked and nothing typed, the search block is the whole
       * panel: nothing above it to be pinned under, and the close button beside
       * its title rather than off in a corner of its own. Read off what the two
       * sections render on rather than from the DOM, since the close button is
       * a child of the panel too and would spoil an :only-child test.
       */
      isSearchOnly() {
        return !(this.cityTerm || this.isSearching);
      },

      /** Only the fold's trigger differs between the two; see isFieldLeading. */
      isSheet() {
        return this.layout === "sheet";
      },

      moreServicesLabel() {
        return this.showAllServices
          ? this.texts.hideMoreServices
          : this.texts.showMoreServices;
      },

      /** Everything that changes how tall the panel wants to be. */
      panelContentKey() {
        return [
          this.isFieldLeading,
          this.isLoading,
          this.isLoadingCities,
          this.cities.length,
          this.visibleServices.length,
        ].join(":");
      },

      /**
       * The service the visitor was last on, remembered from an earlier page
       * type. It leads the list and stands alone until the rest are asked for,
       * so the city's own service is one tap away.
       */
      preferredService() {
        return pickPreferredService(this.services, this.roleTerm);
      },

      resultCount() {
        if (!this.hasLoadedCities) {
          return "";
        }

        if (!this.hasResults) {
          return this.texts.noOptions;
        }

        const template =
          this.cities.length === 1
            ? this.texts.searchResultsSingular
            : this.texts.searchResultsPlural;

        return sprintf(template, this.cities.length, this.query.trim());
      },

      services() {
        return buildCityServices(
          this.cityLinks,
          this.texts,
          this.registeredPostTypes
        );
      },

      /** Picking a first city and swapping an existing one are different jobs. */
      title() {
        return this.cityTerm
          ? this.texts.changeSelectedCity
          : this.texts.chooseYourCity;
      },

      visibleServices() {
        if (!this.preferredService) {
          return this.services;
        }

        if (!this.showAllServices) {
          return [this.preferredService];
        }

        return [
          this.preferredService,
          ...this.services.filter(
            (service) => service.key !== this.preferredService.key
          ),
        ];
      },
    },

    // debounce() in `methods` is one wrapper shared by every instance, so the
    // header dropdown and the mobile sheet would cancel each other's pending
    // search. One timer per panel instead.
    created() {
      this.searchCities = debounce(this.runCitySearch, SEARCH_DEBOUNCE);
    },

    data() {
      return {
        activeIndex: -1,
        isFocused: false,
        locateWidth: null,
        query: "",
        registeredPostTypes: theme_vars?.localConfig?.registeredPostTypes ?? [],
        showAllServices: false,
        texts: theme_vars.cityCaseTypeSelect,
      };
    },

    emits: ["select", "close"],

    methods: {
      ...mapMutations("cities", ["setCities", "setHasLoadedCities"]),

      /**
       * The dropdown is as tall as its content, and `auto` is not a height CSS
       * can transition. So the panel is walked between the two heights its
       * content asks for — the one it has, and the one it takes once the
       * sections have swapped — then let go of again as soon as the walk is
       * over, so it goes on sizing itself to whatever it holds: the matches
       * arriving, a city's services folding out.
       *
       * Written straight to the element rather than through a binding, since
       * the two ends have to reach the DOM as two separate styles and a
       * reactive value would be batched into one. Nothing spills out of the box
       * on the way: the sections shrink and scroll inside whatever height it is
       * passing through. The sheet takes its height from the drawer around it
       * and needs none of this.
       */
      async animatePanelHeight() {
        if (this.isSheet) {
          return;
        }

        const panel = this.$el;
        const from = panel.offsetHeight;

        // The watcher runs before the patch, so this waits for the new content.
        await this.$nextTick();

        panel.style.height = "";
        const to = panel.offsetHeight;

        if (from === to) {
          return;
        }

        panel.style.height = `${from}px`;
        // Reading it back lands the starting height before the ending one,
        // which is the whole of what makes this a transition and not a jump.
        // biome-ignore lint/complexity/noVoid: the read is the point, and void is what says its value is not wanted.
        void panel.offsetHeight;
        panel.style.height = `${to}px`;

        clearTimeout(this.releaseTimer);

        this.releaseTimer = setTimeout(() => {
          panel.style.height = "";
        }, HEIGHT_DURATION);
      },

      // A content-sized box has no length for CSS to animate from, so the width
      // the button is about to give up is read off it while it still has one.
      // Read before the DOM is patched, which is where the watcher runs.
      captureLocateWidth() {
        const button = this.$refs.query?.querySelector(".city-locate__button");

        if (button) {
          this.locateWidth = `${button.offsetWidth}px`;
        }
      },

      chooseActive() {
        const city = this.cities[this.activeIndex];

        if (city) {
          this.select(city);
        }
      },

      clear() {
        this.query = "";
        this.resetResults();
        this.$refs.search?.focus();
      },

      close() {
        this.$emit("close");
      },

      focusSearch() {
        this.$refs.search?.focus();
      },

      moveActive(step) {
        if (!this.hasResults) {
          return;
        }

        const count = this.cities.length;

        // -1 means nothing is active yet, which the wrap-around below cannot
        // step from: entering the list upwards has to land on the last row.
        if (this.activeIndex === -1) {
          this.activeIndex = step > 0 ? 0 : count - 1;
          return;
        }

        this.activeIndex = (this.activeIndex + step + count) % count;
      },

      moveDown() {
        this.moveActive(1);
      },

      moveUp() {
        this.moveActive(-1);
      },

      onInput(event) {
        this.query = event.target.value;
        this.activeIndex = -1;

        if (!this.isSearching) {
          this.resetResults();
          return;
        }

        this.searchCities();
      },

      optionId(index) {
        return `${this.resultsId}-${index}`;
      },

      // Which picker this is, from the layout it was given: the two render the
      // same panel, and a report cannot tell them apart otherwise.
      reportServiceClick(service) {
        pushCityServiceClick(
          this.layout === "sheet" ? "mobileMenu" : "header",
          service,
          this.cityChanged
        );
      },

      resetResults() {
        this.setHasLoadedCities(false);
        this.setCities([]);
      },

      // Called through the per-instance debouncer built in created().
      runCitySearch() {
        if (!this.isSearching) {
          return;
        }

        this.$store.dispatch("cities/getCities", {
          search: this.query.trim(),
        });
      },

      select(value) {
        this.query = "";
        this.activeIndex = -1;
        this.resetResults();
        this.$emit("select", value);
      },

      setActive(index) {
        this.activeIndex = index;
      },

      /** Walks each row in a beat after the one above it, up to a short cap. */
      stagger(index) {
        return { "--stagger": Math.min(index, MAX_STAGGERED_ROWS) };
      },

      toggleServices() {
        this.showAllServices = !this.showAllServices;
      },
    },
    name: "CityPanel",

    props: {
      /**
       * Whether the visitor picked a city in this picker on this page, rather
       * than arriving with one already remembered. It rides along on a service
       * click, where it separates looking up another city and entering a
       * service there from only wanting a different service in the same city.
       */
      cityChanged: {
        default: false,
        type: Boolean,
      },
      cityTerm: {
        default: null,
        type: Object,
      },
      closable: {
        default: false,
        type: Boolean,
      },
      /** `dropdown` floats under the top bar, `sheet` sits inside the mobile menu. */
      layout: {
        default: "dropdown",
        type: String,
      },
    },

    // Both the top bar and the mobile menu render a panel, so the listbox ids
    // have to be unique per instance or aria-controls points at the wrong one.
    setup() {
      const { roleTerm } = useRoleTermLocalStorage();

      return { resultsId: useId(), roleTerm };
    },

    watch: {
      // A new city brings a new set of links, so the fold closes with it.
      cityTerm() {
        this.showAllServices = false;
      },

      isLocateCollapsed(collapsed) {
        if (collapsed) {
          this.captureLocateWidth();
        }
      },

      // The panel is as tall as its content, so every change to that content is
      // a height to walk to.
      panelContentKey() {
        this.animatePanelHeight();
      },
    },
  };
</script>
